import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { chgColor, pct, fmt, regimeColor, scoreMeta } from "../lib/format";
import { Cpu, LayoutDashboard, ListOrdered, Grid3x3, LineChart, Search, LogOut, Activity, Circle, Bell, RefreshCw, ArrowUpRight, ArrowDownRight, Zap, Globe, GitCompareArrows, LayoutGrid, ChevronDown, Menu, X } from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/markets", label: "Markets", icon: LayoutGrid, testid: "nav-markets" },
  { to: "/rankings", label: "Rankings", icon: ListOrdered, testid: "nav-rankings" },
  { to: "/compare", label: "Compare", icon: GitCompareArrows, testid: "nav-compare" },
  { to: "/correlations", label: "Correlations", icon: Grid3x3, testid: "nav-correlations" },
  { to: "/europe", label: "Europe", icon: Globe, testid: "nav-europe" },
  { to: "/global-macro", label: "Macro", icon: Activity, testid: "nav-global-macro" },
  { to: "/portfolio-backtest", label: "Portfolio / Backtest", icon: LineChart, testid: "nav-portfolio" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [regime, setRegime] = useState(null);
  const [ticker, setTicker] = useState([]);
  const [q, setQ] = useState("");
  const [clock, setClock] = useState("");
  const [source, setSource] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [openAlerts, setOpenAlerts] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [openBrowse, setOpenBrowse] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.get("/regime").then((r) => setRegime(r.data)).catch(() => {});
    api.get("/assets").then((r) => setTicker(r.data)).catch(() => {});
    api.get("/data-source").then((r) => setSource(r.data)).catch(() => {});
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    const t = setInterval(() => setClock(new Date().toLocaleTimeString("en-US", { timeZone: "America/New_York", hour12: false })), 1000);
    return () => clearInterval(t);
  }, []);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/refresh");
      setSource(data);
      const [rg, al, as] = await Promise.all([api.get("/regime"), api.get("/alerts"), api.get("/assets")]);
      setRegime(rg.data); setAlerts(al.data); setTicker(as.data);
      toast.success(`Data refreshed · ${data.mode === "live" ? `${data.live_symbols.length} live symbols` : "simulated"}`);
    } catch (e) {
      toast.error("Refresh failed");
    } finally { setRefreshing(false); }
  };

  const go = (sym) => {
    if (!sym) return;
    nav(`/asset/${sym.toUpperCase()}`);
    setQ(""); setOpenSearch(false);
  };

  const search = (e) => {
    e.preventDefault();
    const first = matches[0];
    go(q.trim() ? (first ? first.symbol : q.trim()) : null);
  };

  const matches = q.trim()
    ? ticker.filter((t) => {
        const s = q.trim().toLowerCase();
        return t.symbol.toLowerCase().includes(s) || (t.name || "").toLowerCase().includes(s);
      }).slice(0, 7)
    : [];

  const tickSyms = ["SPY", "QQQ", "GLD", "BTC", "VIX", "DXY", "USO", "TLT", "US10Y"];
  const tickRows = ticker.filter((t) => tickSyms.includes(t.symbol));

  const EU_CC = new Set(["DE", "FR", "IT", "ES", "EU", "GB"]);
  const BROWSE_CATS = [
    ["US Equities", (a) => a.asset_class === "equity"],
    ["US ETFs", (a) => a.asset_class === "etf_equity" && !EU_CC.has(a.country)],
    ["European Indices", (a) => a.asset_class === "etf_equity" && EU_CC.has(a.country)],
    ["Forex", (a) => a.asset_class === "forex"],
    ["Commodities", (a) => a.asset_class === "commodity"],
    ["Crypto", (a) => a.asset_class === "crypto"],
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* top bar */}
      <header className="sticky top-0 z-30 flex items-center gap-4 px-4 h-14 border-b border-[#1E293B] bg-[#080A0F]/95 backdrop-blur">
        <button data-testid="mobile-menu-button" onClick={() => setOpenMobile(true)}
          className="md:hidden h-9 w-9 -ml-1 rounded flex items-center justify-center border border-[#334155] text-slate-300">
          <Menu size={18} />
        </button>
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded flex items-center justify-center" style={{ background: "#00E5FF18" }}>
            <Cpu size={18} className="text-[#00E5FF]" />
          </div>
          <div className="leading-none">
            <div className="font-display text-lg font-extrabold tracking-tight">AURA QUANT<span className="text-[#00E5FF]"> //</span> TERMINAL</div>
          </div>
        </div>

        {regime && (
          <div data-testid="header-regime-ticker" className="hidden md:flex items-center gap-2 pl-4 ml-1 border-l border-[#1E293B]">
            <Circle size={8} className="live-dot" style={{ color: regimeColor(regime.regime), fill: regimeColor(regime.regime) }} />
            <span className="eyebrow">Regime</span>
            <span className="font-display font-bold text-sm" style={{ color: regimeColor(regime.regime) }}>{regime.label}</span>
            <span className="font-mono text-xs text-slate-400">{regime.confidence}%</span>
          </div>
        )}

        <div className="ml-auto flex items-center gap-3">
        <div className="relative hidden md:block" data-testid="browse-menu"
          onMouseLeave={() => setOpenBrowse(false)}>
          <button data-testid="browse-menu-button" onClick={() => setOpenBrowse((v) => !v)}
            className="h-8 px-3 rounded-md flex items-center gap-1.5 border border-[#334155] hover:bg-[#00E5FF12] hover:border-[#00E5FF44] hover:text-[#00E5FF] transition-colors text-xs font-mono text-slate-300">
            <LayoutGrid size={13} /> Browse <ChevronDown size={12} className={openBrowse ? "rotate-180 transition-transform" : "transition-transform"} />
          </button>
          {openBrowse && (
            <div data-testid="browse-menu-dropdown" className="absolute right-0 top-10 w-[380px] max-h-[70vh] overflow-y-auto panel rounded-lg shadow-2xl z-50 p-3">
              {BROWSE_CATS.map(([label, fn]) => {
                const items = ticker.filter(fn).sort((x, y) => y.smart_quant_score - x.smart_quant_score);
                if (!items.length) return null;
                return (
                  <div key={label} className="mb-3 last:mb-0">
                    <div className="eyebrow mb-1.5">{label}</div>
                    <div className="grid grid-cols-2 gap-1">
                      {items.map((it) => {
                        const sm = scoreMeta(it.smart_quant_score);
                        return (
                          <button key={it.symbol} data-testid={`browse-item-${it.symbol}`}
                            onClick={() => { nav(`/asset/${it.symbol}`); setOpenBrowse(false); }}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[#141C2E] transition-colors text-left">
                            <span className="font-mono font-bold text-[11px] w-14 shrink-0">{it.symbol}</span>
                            <span className="text-[10px] text-slate-500 truncate flex-1">{it.name}</span>
                            <span className="font-mono text-[10px] font-bold tnum shrink-0" style={{ color: sm.color }}>{fmt(it.smart_quant_score, 0)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <form onSubmit={search} className="relative hidden sm:flex items-center gap-1.5" data-testid="asset-search-form">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="asset-search-input" value={q}
              onChange={(e) => { setQ(e.target.value); setOpenSearch(true); }}
              onFocus={() => setOpenSearch(true)}
              onBlur={() => setTimeout(() => setOpenSearch(false), 150)}
              placeholder="Search ticker or name…"
              className="panel-2 rounded-md pl-8 pr-3 py-1.5 text-xs w-56 outline-none focus:border-[#00E5FF] transition-colors font-mono" />
            {openSearch && matches.length > 0 && (
              <div data-testid="asset-search-dropdown" className="absolute left-0 top-10 w-72 max-h-[360px] overflow-y-auto panel rounded-lg shadow-2xl z-50 p-1.5">
                {matches.map((mt) => {
                  const sm = scoreMeta(mt.smart_quant_score);
                  return (
                    <button key={mt.symbol} type="button" data-testid={`search-result-${mt.symbol}`}
                      onMouseDown={(e) => { e.preventDefault(); go(mt.symbol); }}
                      className="w-full text-left flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-[#141C2E] transition-colors">
                      <span className="font-mono font-bold text-xs w-14 shrink-0">{mt.symbol}</span>
                      <span className="text-[11px] text-slate-400 truncate flex-1">{mt.name}</span>
                      <span className="font-mono text-[11px] font-bold tnum px-1.5 py-0.5 rounded shrink-0"
                        style={{ color: sm.color, background: `${sm.color}18` }}>{fmt(mt.smart_quant_score, 0)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button type="submit" data-testid="asset-search-button" title="Search"
            className="h-8 px-3 rounded-md flex items-center gap-1.5 border border-[#334155] hover:bg-[#00E5FF12] hover:border-[#00E5FF44] hover:text-[#00E5FF] transition-colors text-xs font-mono text-slate-300">
            <Search size={13} /> <span className="hidden lg:inline">Search</span>
          </button>
        </form>

        {source && (
          <div data-testid="data-source-badge" className="hidden lg:flex items-center gap-1.5 px-2 py-1 rounded-md border font-mono text-[10px] uppercase tracking-wider"
            style={{ borderColor: source.mode === "live" ? "#10B98155" : "#F59E0B55", color: source.mode === "live" ? "#10B981" : "#F59E0B", background: source.mode === "live" ? "#10B98110" : "#F59E0B10" }}>
            <Zap size={11} className={source.mode === "live" ? "live-dot" : ""} />
            {source.mode === "live" ? `LIVE · ${source.live_symbols.length + source.live_series.length + (source.live_eu_series?.length || 0) + (source.live_fundamentals?.length || 0)} feeds` : "SIM"}
          </div>
        )}
        <button data-testid="refresh-data-button" onClick={doRefresh} disabled={refreshing}
          title={source?.auto_refresh?.enabled ? `Refresh now · auto-refresh daily at ${String(source.auto_refresh.hour_utc).padStart(2, "0")}:00 UTC` : "Refresh market data"}
          className="h-8 w-8 rounded flex items-center justify-center border border-[#334155] hover:bg-[#141C2E] transition-colors disabled:opacity-50 relative">
          <RefreshCw size={14} className={`text-slate-300 ${refreshing ? "spin" : ""}`} />
          {source?.auto_refresh?.enabled && (
            <span className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-[#10B981] live-dot" title="auto-refresh on" />
          )}
        </button>

        {/* alerts bell */}
        <div className="relative">
          <button data-testid="alerts-bell" onClick={() => setOpenAlerts((v) => !v)}
            className="h-8 w-8 rounded flex items-center justify-center border border-[#334155] hover:bg-[#141C2E] transition-colors relative">
            <Bell size={15} className="text-slate-300" />
            {alerts && alerts.alerts?.length > 0 && (
              <span data-testid="alerts-count" className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-bold flex items-center justify-center">{alerts.alerts.length}</span>
            )}
          </button>
          {openAlerts && (
            <div data-testid="alerts-dropdown" className="absolute right-0 top-11 w-80 max-h-[420px] overflow-y-auto panel rounded-lg shadow-2xl z-50 p-2">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="eyebrow">Signal Alerts</span>
                <span className="font-mono text-[10px] text-slate-500">{alerts?.lookback_days}d lookback</span>
              </div>
              {(!alerts || alerts.alerts.length === 0) && <div className="px-2 py-4 text-xs text-slate-500 text-center">No new flips detected</div>}
              {alerts?.alerts.map((a, i) => (
                <button key={i} data-testid={`alert-item-${i}`}
                  onClick={() => { setOpenAlerts(false); if (a.symbol) nav(`/asset/${a.symbol}`); }}
                  className="w-full text-left flex items-start gap-2 px-2 py-2 rounded-md hover:bg-[#141C2E] transition-colors">
                  {a.type === "regime"
                    ? <Activity size={15} className="mt-0.5 shrink-0" style={{ color: a.region === "EA" ? "#22D3EE" : regimeColor(regime?.regime) }} />
                    : a.direction === "upgrade"
                      ? <ArrowUpRight size={15} className="mt-0.5 shrink-0 text-[#10B981]" />
                      : <ArrowDownRight size={15} className="mt-0.5 shrink-0 text-[#EF4444]" />}
                  <div className="min-w-0">
                    {a.type === "regime"
                      ? <><div className="text-xs font-semibold text-slate-100">{a.region === "EA" ? "Eurozone" : "US"} regime shift</div>
                          <div className="text-[11px] text-slate-400">{a.from} → <span className="text-slate-200">{a.to}</span></div></>
                      : <><div className="text-xs font-semibold text-slate-100">{a.symbol} · <span style={{ color: a.direction === "upgrade" ? "#10B981" : "#EF4444" }}>{a.direction}</span></div>
                          <div className="text-[11px] text-slate-400">{a.from} → <span className="text-slate-200">{a.to}</span> · {a.score_prev}→{a.score_now}</div></>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 font-mono text-[11px] text-slate-400">
          <Activity size={13} className="text-[#10B981] live-dot" />
          <span className="hidden xl:inline">{clock} NY</span>
        </div>
        <div className="flex items-center gap-2 pl-3 border-l border-[#1E293B]">
          <div className="hidden md:block text-right leading-none">
            <div className="text-xs font-semibold">{user?.name || "Analyst"}</div>
            <div className="eyebrow">{user?.role || "user"}</div>
          </div>
          <button data-testid="logout-button" onClick={() => { logout(); nav("/login"); }}
            className="h-8 w-8 rounded flex items-center justify-center border border-[#334155] hover:bg-[#141C2E] transition-colors">
            <LogOut size={15} className="text-slate-300" />
          </button>
        </div>
        </div>
      </header>

      {/* mobile drawer */}
      {openMobile && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpenMobile(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-[82%] max-w-[340px] bg-[#0A0E16] border-r border-[#1E293B] flex flex-col overflow-y-auto">
            <div className="flex items-center justify-between px-4 h-14 border-b border-[#1E293B] shrink-0">
              <div className="font-display text-base font-extrabold">AURA QUANT<span className="text-[#00E5FF]"> //</span></div>
              <button data-testid="mobile-drawer-close" onClick={() => setOpenMobile(false)}
                className="h-9 w-9 rounded flex items-center justify-center border border-[#334155] text-slate-300"><X size={18} /></button>
            </div>
            <div className="p-3">
              <form onSubmit={(e) => { search(e); setOpenMobile(false); }} className="relative mb-3">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="mobile-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search ticker or name…"
                  className="panel-2 rounded-md pl-8 pr-3 py-2 text-sm w-full outline-none focus:border-[#00E5FF] font-mono" />
              </form>
              <div className="space-y-1 mb-4">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} data-testid={`mobile-${n.testid}`} end={n.to === "/"}
                    onClick={() => setOpenMobile(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-colors ${isActive ? "bg-[#00E5FF12] text-[#00E5FF] border border-[#00E5FF33]" : "text-slate-300 hover:bg-[#141C2E] border border-transparent"}`}>
                    <n.icon size={17} /> {n.label}
                  </NavLink>
                ))}
              </div>
              <div className="eyebrow px-1 mb-2">Browse Markets</div>
              {BROWSE_CATS.map(([label, fn]) => {
                const items = ticker.filter(fn).sort((x, y) => y.smart_quant_score - x.smart_quant_score);
                if (!items.length) return null;
                return (
                  <div key={label} className="mb-3">
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-1 px-1">{label}</div>
                    <div className="grid grid-cols-2 gap-1">
                      {items.map((it) => {
                        const sm = scoreMeta(it.smart_quant_score);
                        return (
                          <button key={it.symbol} data-testid={`mobile-browse-${it.symbol}`}
                            onClick={() => { nav(`/asset/${it.symbol}`); setOpenMobile(false); }}
                            className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-[#141C2E] text-left">
                            <span className="font-mono font-bold text-[11px] w-12 shrink-0">{it.symbol}</span>
                            <span className="font-mono text-[10px] font-bold tnum ml-auto shrink-0" style={{ color: sm.color }}>{fmt(it.smart_quant_score, 0)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* sidebar */}
        <aside className="hidden md:flex flex-col w-52 border-r border-[#1E293B] bg-[#0A0E16] p-3 gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} end={n.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-[#00E5FF12] text-[#00E5FF] border border-[#00E5FF33]" : "text-slate-400 hover:text-slate-100 hover:bg-[#141C2E] border border-transparent"}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
          <div className="mt-auto eyebrow px-2 pt-4">engine heartbeat</div>
          <div className="px-2 flex items-center gap-2 text-[11px] font-mono text-[#10B981]">
            <span className="live-dot">●</span> all systems nominal
          </div>
        </aside>

        {/* main */}
        <main className="flex-1 min-w-0 overflow-x-hidden p-4 md:p-6 pb-16">
          <Outlet />
        </main>
      </div>

      {/* footer ticker */}
      <footer className="fixed bottom-0 left-0 right-0 h-8 border-t border-[#1E293B] bg-[#080A0F] overflow-hidden z-30">
        <div className="flex whitespace-nowrap h-full items-center">
          <div className="marquee flex gap-8 px-6">
            {[...tickRows, ...tickRows].map((t, i) => (
              <span key={i} className="flex items-center gap-2 font-mono text-[11px]">
                <span className="text-slate-300 font-semibold">{t.symbol}</span>
                <span className="tnum text-slate-400">{fmt(t.price, 2)}</span>
                <span className="tnum" style={{ color: chgColor(t.change_1d) }}>{pct(t.change_1d, 2)}</span>
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
